import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Alert, Button, Icon, Input, message, Popconfirm } from 'antd';
import { withTranslation } from 'react-i18next';
import { AppStoreContext } from '../../context/appStoreContext';
import { MANAGE_PRINT_OPTION_CATALOG as trans } from './TranslationValues';
import {
  PRINT_CATALOG_DIMENSION,
  PRINT_CATALOG_STATUS,
  createOrgPrintCatalogEntry,
  deleteOrgPrintCatalogEntry,
  fetchOrgPrintCatalogMap,
  getOrganizationIdForPrintCatalog,
  sortPrintCatalogEntries,
  updateOrgPrintCatalogEntry,
} from '../../utils/services/printDefaultsAPI';
import { LoaderContainer } from './styles';
import Loader from '../../components/Loader';
import {
  AddField,
  AddRow,
  CatalogCard,
  CatalogCardHead,
  CatalogCardTitleBlock,
  CatalogIntro,
  CatalogPage,
  CatalogRow,
  CountBadge,
  RowActions,
  RowIndex,
  RowLabel,
  RowMain,
  RowValue,
} from './PrintOptionCatalog.styles';

const NAMESPACE = ['MANAGE_PRINT_OPTION_CATALOG'];

const DIMENSION_CONFIG = [
  {
    dimension: PRINT_CATALOG_DIMENSION.COLOR_MODE,
    accent: '#7c5cbf',
    titleKey: 'COLOR_MODE_TITLE',
    descKey: 'COLOR_MODE_DESC',
    valuePhKey: 'VALUE_PLACEHOLDER_COLOR_MODE',
    displayPhKey: 'DISPLAY_LABEL_PLACEHOLDER_COLOR_MODE',
  },
  {
    dimension: PRINT_CATALOG_DIMENSION.MAIL_TYPE,
    accent: '#3d7aed',
    titleKey: 'MAIL_TYPE_TITLE',
    descKey: 'MAIL_TYPE_DESC',
    valuePhKey: 'VALUE_PLACEHOLDER_MAIL_TYPE',
    displayPhKey: 'DISPLAY_LABEL_PLACEHOLDER_MAIL_TYPE',
  },
  {
    dimension: PRINT_CATALOG_DIMENSION.ENVELOPE_TYPE,
    accent: '#e67a22',
    titleKey: 'ENVELOPE_TYPE_TITLE',
    descKey: 'ENVELOPE_TYPE_DESC',
    valuePhKey: 'VALUE_PLACEHOLDER_ENVELOPE_TYPE',
    displayPhKey: 'DISPLAY_LABEL_PLACEHOLDER_ENVELOPE_TYPE',
  },
];

const emptyForms = () => ({
  [PRINT_CATALOG_DIMENSION.COLOR_MODE]: { value: '', displayLabel: '' },
  [PRINT_CATALOG_DIMENSION.MAIL_TYPE]: { value: '', displayLabel: '' },
  [PRINT_CATALOG_DIMENSION.ENVELOPE_TYPE]: { value: '', displayLabel: '' },
});

const PrintOptionCatalog = ({ t }) => {
  const { userDetails } = useContext(AppStoreContext);
  const organizationId = getOrganizationIdForPrintCatalog(userDetails);

  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState({
    [PRINT_CATALOG_DIMENSION.COLOR_MODE]: [],
    [PRINT_CATALOG_DIMENSION.MAIL_TYPE]: [],
    [PRINT_CATALOG_DIMENSION.ENVELOPE_TYPE]: [],
  });
  const [forms, setForms] = useState(() => emptyForms());
  const [busyDimension, setBusyDimension] = useState(null);

  const loadAll = useCallback(async (opts = {}) => {
    const { silent = false } = opts;
    if (!organizationId) {
      setLoading(false);
      return;
    }
    if (!silent) {
      setLoading(true);
    }
    try {
      const map = await fetchOrgPrintCatalogMap(organizationId);
      setEntries({
        [PRINT_CATALOG_DIMENSION.COLOR_MODE]: map[PRINT_CATALOG_DIMENSION.COLOR_MODE] || [],
        [PRINT_CATALOG_DIMENSION.MAIL_TYPE]: map[PRINT_CATALOG_DIMENSION.MAIL_TYPE] || [],
        [PRINT_CATALOG_DIMENSION.ENVELOPE_TYPE]: map[PRINT_CATALOG_DIMENSION.ENVELOPE_TYPE] || [],
      });
    } catch (e) {
      message.error(t(trans.FETCH_ERROR.key, trans.FETCH_ERROR.value));
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [organizationId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const setFormField = (dimension, field, value) => {
    setForms(prev => ({
      ...prev,
      [dimension]: { ...prev[dimension], [field]: value },
    }));
  };

  const handleAdd = async dimension => {
    const { value, displayLabel } = forms[dimension] || {};
    const v = value != null ? String(value).trim() : '';
    const d = displayLabel != null ? String(displayLabel).trim() : '';
    if (!v || !d) {
      message.warning(t(trans.FIELD_REQUIRED.key, trans.FIELD_REQUIRED.value));
      return;
    }
    if (!organizationId) {
      return;
    }
    const list = entries[dimension] || [];
    const maxOrder = list.reduce((m, row) => Math.max(m, Number(row.sortOrder) || 0), 0);
    const sortOrder = maxOrder > 0 ? maxOrder + 10 : 10;
    setBusyDimension(dimension);
    try {
      const res = await createOrgPrintCatalogEntry(organizationId, {
        dimension,
        value: v,
        displayLabel: d,
        sortOrder,
        active: true,
      });
      if (res && res.statusCode === PRINT_CATALOG_STATUS.MUTATION_SUCCESS) {
        message.success(t(trans.ADD_SUCCESS.key, trans.ADD_SUCCESS.value));
        setFormField(dimension, 'value', '');
        setFormField(dimension, 'displayLabel', '');
        await loadAll({ silent: true });
      } else {
        message.error(t(trans.SAVE_ERROR.key, trans.SAVE_ERROR.value));
      }
    } catch (e) {
      message.error(t(trans.SAVE_ERROR.key, trans.SAVE_ERROR.value));
    } finally {
      setBusyDimension(null);
    }
  };

  const handleDelete = async (dimension, entryId) => {
    if (!organizationId) {
      return;
    }
    setBusyDimension(dimension);
    try {
      const res = await deleteOrgPrintCatalogEntry(organizationId, entryId);
      if (res && res.statusCode === PRINT_CATALOG_STATUS.MUTATION_SUCCESS) {
        message.success(t(trans.DELETE_SUCCESS.key, trans.DELETE_SUCCESS.value));
        await loadAll({ silent: true });
      } else {
        message.error(t(trans.SAVE_ERROR.key, trans.SAVE_ERROR.value));
      }
    } catch (e) {
      message.error(t(trans.SAVE_ERROR.key, trans.SAVE_ERROR.value));
    } finally {
      setBusyDimension(null);
    }
  };

  const persistSortOrders = async (dimension, reordered) => {
    if (!organizationId) {
      return;
    }
    // Apply new sortOrder from visual order only — do not re-sort by old sortOrder or swaps are lost.
    const withNewOrder = reordered.map((row, index) => ({
      ...row,
      sortOrder: (index + 1) * 10,
    }));
    const previous = entries[dimension] || [];
    const prevById = new Map(previous.map(r => [String(r.id), r]));
    setBusyDimension(dimension);
    try {
      for (const row of withNewOrder) {
        const entryId = row.id != null ? row.id : row.entryId;
        const prevRow = entryId != null ? prevById.get(String(entryId)) : null;
        if (!prevRow || Number(prevRow.sortOrder) !== Number(row.sortOrder)) {
          if (entryId == null) {
            throw new Error('reorder-missing-id');
          }
          const res = await updateOrgPrintCatalogEntry(organizationId, entryId, { sortOrder: row.sortOrder });
          if (!res || res.statusCode !== PRINT_CATALOG_STATUS.MUTATION_SUCCESS) {
            throw new Error('reorder-failed');
          }
        }
      }
      message.success(t(trans.REORDER_SUCCESS.key, trans.REORDER_SUCCESS.value));
      await loadAll({ silent: true });
    } catch (e) {
      message.error(t(trans.SAVE_ERROR.key, trans.SAVE_ERROR.value));
      await loadAll({ silent: true });
    } finally {
      setBusyDimension(null);
    }
  };

  const moveRow = async (dimension, index, delta) => {
    const list = sortPrintCatalogEntries([...(entries[dimension] || [])]);
    const j = index + delta;
    if (j < 0 || j >= list.length) {
      return;
    }
    const next = [...list];
    const tmp = next[index];
    next[index] = next[j];
    next[j] = tmp;
    await persistSortOrders(dimension, next);
  };

  if (!organizationId) {
    return (
      <CatalogPage>
        <CatalogIntro>
          <h5>{t(trans.TITLE.key, trans.TITLE.value)}</h5>
        </CatalogIntro>
        <Alert type="warning" message={t(trans.ORG_CONTEXT_REQUIRED.key, trans.ORG_CONTEXT_REQUIRED.value)} showIcon />
      </CatalogPage>
    );
  }

  if (loading) {
    return (
      <LoaderContainer>
        <Loader />
      </LoaderContainer>
    );
  }

  return (
    <CatalogPage>
      <CatalogIntro>
        <h5>{t(trans.TITLE.key, trans.TITLE.value)}</h5>
        <p>{t(trans.DESCRIPTION.key, trans.DESCRIPTION.value)}</p>
      </CatalogIntro>

      {DIMENSION_CONFIG.map(cfg => {
        const list = sortPrintCatalogEntries(entries[cfg.dimension] || []);
        const form = forms[cfg.dimension] || { value: '', displayLabel: '' };
        const busy = busyDimension === cfg.dimension;

        return (
          <CatalogCard key={cfg.dimension} accent={cfg.accent}>
            <CatalogCardHead>
              <CatalogCardTitleBlock>
                <h6>{t(trans[cfg.titleKey].key, trans[cfg.titleKey].value)}</h6>
                <p>{t(trans[cfg.descKey].key, trans[cfg.descKey].value)}</p>
              </CatalogCardTitleBlock>
              <CountBadge>
                {t(trans.COUNT_VALUES.key, trans.COUNT_VALUES.value, { count: list.length })}
              </CountBadge>
            </CatalogCardHead>

            {list.length === 0 ? (
              <CatalogRow style={{ color: 'rgba(30, 41, 51, 0.55)', fontSize: 13 }}>
                {t(trans.EMPTY_LIST.key, trans.EMPTY_LIST.value)}
              </CatalogRow>
            ) : (
              list.map((row, index) => (
                <CatalogRow key={row.id}>
                  <RowIndex>{index + 1}</RowIndex>
                  <RowMain>
                    <RowValue>{row.value}</RowValue>
                    <RowLabel>{row.displayLabel}</RowLabel>
                  </RowMain>
                  <RowActions>
                    <Button
                      size="small"
                      disabled={busy || index === 0}
                      onClick={() => moveRow(cfg.dimension, index, -1)}
                      title={t(trans.MOVE_UP.key, trans.MOVE_UP.value)}
                    >
                      <Icon type="up" />
                    </Button>
                    <Button
                      size="small"
                      disabled={busy || index === list.length - 1}
                      onClick={() => moveRow(cfg.dimension, index, 1)}
                      title={t(trans.MOVE_DOWN.key, trans.MOVE_DOWN.value)}
                    >
                      <Icon type="down" />
                    </Button>
                    <Popconfirm
                      title={t(trans.DELETE_CONFIRM.key, trans.DELETE_CONFIRM.value)}
                      onConfirm={() => handleDelete(cfg.dimension, row.id)}
                      okText={t(trans.DELETE.key, trans.DELETE.value)}
                      cancelText="Cancel"
                    >
                      <Button size="small" type="danger" ghost disabled={busy} title={t(trans.DELETE.key, trans.DELETE.value)}>
                        <Icon type="delete" />
                      </Button>
                    </Popconfirm>
                  </RowActions>
                </CatalogRow>
              ))
            )}

            <AddRow>
              <AddField>
                <label htmlFor={`catalog-value-${cfg.dimension}`}>{t(trans.VALUE_LABEL.key, trans.VALUE_LABEL.value)}</label>
                <Input
                  id={`catalog-value-${cfg.dimension}`}
                  value={form.value}
                  onChange={e => setFormField(cfg.dimension, 'value', e.target.value)}
                  disabled={busy}
                  placeholder={t(trans[cfg.valuePhKey].key, trans[cfg.valuePhKey].value)}
                />
              </AddField>
              <AddField>
                <label htmlFor={`catalog-label-${cfg.dimension}`}>
                  {t(trans.DISPLAY_LABEL_LABEL.key, trans.DISPLAY_LABEL_LABEL.value)}
                </label>
                <Input
                  id={`catalog-label-${cfg.dimension}`}
                  value={form.displayLabel}
                  onChange={e => setFormField(cfg.dimension, 'displayLabel', e.target.value)}
                  disabled={busy}
                  placeholder={t(trans[cfg.displayPhKey].key, trans[cfg.displayPhKey].value)}
                />
              </AddField>
              <Button type="primary" className="add_user_btn" onClick={() => handleAdd(cfg.dimension)} loading={busy} icon="plus">
                {t(trans.ADD_BUTTON.key, trans.ADD_BUTTON.value)}
              </Button>
            </AddRow>
          </CatalogCard>
        );
      })}
    </CatalogPage>
  );
};

export default withTranslation(NAMESPACE)(PrintOptionCatalog);
